import { Path, PathValue, UseFormReturn } from 'react-hook-form';
import { formatDate } from '../../../../../../lib/hooks/utils';
import { FormControl, FormField, FormItem, FormMessage } from '../../../../../../ui/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../ui/components/ui/select';
import { CapacityBySlotsNeededResponseDto, CapacityMetaResponseDto } from '../../../../../register/checkout/step2/types/response';
import { BasePaymentDataSchemaType } from '../../data-schemas/payment-data-schema';
import { useEffect, useState } from 'react';
import { Input } from '../../../../../../ui/components/ui/input';

interface StartDateSelectorProps<T extends BasePaymentDataSchemaType> {
  form: UseFormReturn<T>;
  capacities: CapacityMetaResponseDto;
  currentCapacityValue?: number;
  isAdmin?: boolean;
}

export const StartDateSelector = <T extends BasePaymentDataSchemaType>({
  form,
  capacities,
  currentCapacityValue = undefined,
  isAdmin = false
}: StartDateSelectorProps<T>) => {
  const initialValue = form.getValues('newCustomerDate' as Path<T>) as string | undefined;
  const [chooseDate, setChooseDate] = useState(!!initialValue && initialValue.includes('chooseDate'));

  // Beim Mount: wenn der Form-Value den 'chooseDate '-Prefix enthält, einmalig auf den reinen ISO-String normalisieren.
  // Sonst landet beim Submit der Prefix in der API und das Date schlägt fehl.
  useEffect(() => {
    if (initialValue && initialValue.startsWith('chooseDate ')) {
      const cleaned = initialValue.slice('chooseDate '.length);
      form.setValue('newCustomerDate' as Path<T>, cleaned as PathValue<T, Path<T>>, { shouldDirty: false, shouldTouch: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full">
      <FormField
        control={form.control}
        name={'newCustomerDate' as Path<T>}
        render={({ field }) => {
          const currentCapacity =
            currentCapacityValue && !isAdmin
              ? capacities.capacityBySlotsNeeded.find((value) => value.slotsNeeded === currentCapacityValue)
              : capacities.capacityBySlotsNeeded[0];

          return (
            <div className="flex flex-col gap-2">
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  {currentCapacity && currentCapacity.capacity.length > 0 ? (
                    <Select
                      onValueChange={(value) => {
                        if (value === 'chooseDate') {
                          setChooseDate(true);
                          return;
                        }
                        setChooseDate(false);
                        field.onChange(value);
                        form.trigger('newCustomerDate' as Path<T>);
                      }}
                      defaultValue={field.value as string}
                      value={chooseDate ? 'chooseDate' : (field.value as string)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Startdatum auswählen *" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItems currentCapacity={currentCapacity} />
                        {isAdmin && <SelectItem value="chooseDate">Datum frei wählen</SelectItem>}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-red-500">
                      <p>Keine freien Slots verfügbar.</p>
                    </div>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>

              {chooseDate && isAdmin && (
                <FormField
                  control={form.control}
                  name={'newCustomerDate' as Path<T>}
                  render={({ field }) => {
                    const fieldValue = (field.value as string) ?? '';
                    const cleaned = fieldValue.startsWith('chooseDate ') ? fieldValue.slice('chooseDate '.length) : fieldValue;
                    const date = cleaned ? new Date(cleaned) : new Date();

                    return (
                      <FormItem className="flex flex-col bg-white rounded-lg">
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Datum frei wählen *"
                            type="date"
                            value={isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]}
                            onChange={(e) => {
                              field.onChange(e);
                              form.trigger('newCustomerDate' as Path<T>);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

const SelectItems = ({ currentCapacity }: { currentCapacity: CapacityBySlotsNeededResponseDto }) => {
  return (
    <>
      {currentCapacity.capacity.map((value) => {
        const color = value.freeCapacitySlots < 7 ? (value.freeCapacitySlots < 4 ? '#CE0909' : '#FFBF00') : '#C0DE60';
        return (
          <SelectItem key={value.date} value={value.date}>
            <div className="flex items-center gap-1 text-sm">
              <div style={{ color }} className="w-2.5 h-2.5">
                <svg className="w-2.5 h-2.5" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="50" cy="50" rx="50" ry="50" fill="currentColor" />
                </svg>
              </div>
              <span>
                {`${formatDate(value.date)} (${value.freeCapacitySlots} ${value.freeCapacitySlots === 1 ? 'freier Platz' : 'freie Plätze'})`}
              </span>
            </div>
          </SelectItem>
        );
      })}
    </>
  );
};
